import React, { useState } from "react";
import { RefreshCw, Wrench } from "lucide-react";
import { toast } from "react-toastify";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import api from "~/services/api";

const normalizeRow = (row) => ({
  ...row,
  selected: false,
  session_start_date: row.session_start_date || "",
  session_end_date: row.session_end_date || "",
  plan_schedule: row.plan_schedule || "",
  tuition_amount: Number(row.tuition_amount || 0),
  tuition_discount: Number(row.tuition_discount || 0),
  payment_type: row.payment_type || "full",
});

export default function GlobalSessionRepair() {
  const [rows, setRows] = useState([]);
  const [identified, setIdentified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [preserveInvoices, setPreserveInvoices] = useState(true);

  const selectedRows = rows.filter((row) => row.selected);

  async function identify() {
    setLoading(true);
    try {
      const { data } = await api.get("/data-sync/global-sessions/missing");
      setRows((data?.rows || []).map(normalizeRow));
      setIdentified(true);
    } catch (err) {
      toast(
        err?.response?.data?.error || "Failed to identify missing Global sessions.",
        { type: "error", autoClose: 4000 }
      );
    } finally {
      setLoading(false);
    }
  }

  function updateRow(contractId, field, value) {
    setRows((current) =>
      current.map((row) =>
        row.contract_id === contractId ? { ...row, [field]: value } : row
      )
    );
  }

  async function repair() {
    if (!selectedRows.length) {
      toast("Select at least one contract.", { type: "warning", autoClose: 3000 });
      return;
    }

    const missingDates = selectedRows.some(
      (row) => !row.session_start_date || !row.session_end_date
    );
    if (missingDates) {
      toast("Fill the start and end dates for every selected student.", {
        type: "warning",
        autoClose: 4000,
      });
      return;
    }

    const invalidDates = selectedRows.some(
      (row) => row.session_start_date > row.session_end_date
    );
    if (invalidDates) {
      toast("Session end date cannot be before the start date.", {
        type: "warning",
        autoClose: 4000,
      });
      return;
    }

    const confirmed = window.confirm(
      `Create only the missing Contract Session for ${selectedRows.length} selected contract(s)? Existing invoices will not be created or changed.`
    );
    if (!confirmed) return;

    setRepairing(true);
    try {
      const { data } = await api.post("/data-sync/global-sessions/repair", {
        preserve_invoices: preserveInvoices,
        sessions: selectedRows.map((row) => ({
          contract_id: row.contract_id,
          session_start_date: row.session_start_date,
          session_end_date: row.session_end_date,
          plan_schedule: row.plan_schedule || null,
          payment_type: row.payment_type,
          tuition_amount: Number(row.tuition_amount || 0),
          tuition_discount: Number(row.tuition_discount || 0),
        })),
      });
      toast(
        `${data?.repairedCount || 0} Global session(s) created successfully.`,
        { type: "success", autoClose: 4000 }
      );
      await identify();
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to repair Global sessions.", {
        type: "error",
        autoClose: 5000,
      });
    } finally {
      setRepairing(false);
    }
  }

  return (
    <InputLineGroup title="Repair Global Sessions" activeMenu={true}>
      <div className="flex flex-col gap-4 py-2 min-w-0">
        <div className="flex gap-2">
          <button
            type="button"
            disabled={loading || repairing}
            onClick={identify}
            className="text-md font-bold bg-secondary border hover:border-primary hover:text-primary rounded-md px-4 h-9 flex items-center justify-center text-xs gap-2 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            {loading ? "Identifying..." : "Identify missing sessions"}
          </button>
          {identified && rows.length > 0 && (
            <button
              type="button"
              disabled={
                repairing ||
                loading ||
                selectedRows.length === 0 ||
                !preserveInvoices
              }
              onClick={repair}
              className="text-md font-bold bg-primary text-white border rounded-md px-4 h-9 flex items-center justify-center text-xs gap-2 disabled:opacity-50"
            >
              <Wrench size={14} />
              {repairing
                ? "Repairing..."
                : `Repair selected (${selectedRows.length})`}
            </button>
          )}
        </div>

        {identified && rows.length > 0 && (
          <label className="flex items-start gap-2 rounded-lg border p-3 text-xs max-w-2xl cursor-pointer">
            <input
              type="checkbox"
              checked={preserveInvoices}
              onChange={(event) => setPreserveInvoices(event.target.checked)}
              className="mt-0.5"
            />
            <span>
              <strong>Do not create or change invoices</strong>
            </span>
          </label>
        )}

        {identified && rows.length === 0 && (
          <div className="rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-800">
            No active Global contracts without a session were found.
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-auto border rounded-lg">
            <table className="min-w-[1150px] w-full text-xs">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="p-2">Sel.</th>
                  <th className="p-2">Student</th>
                  <th className="p-2">Branch</th>
                  <th className="p-2">Start date *</th>
                  <th className="p-2">End date *</th>
                  <th className="p-2">Plan schedule</th>
                  <th className="p-2">Tuition</th>
                  <th className="p-2">Discount</th>
                  <th className="p-2">Payment</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.contract_id} className="border-t align-top">
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={(event) =>
                          updateRow(row.contract_id, "selected", event.target.checked)
                        }
                      />
                    </td>
                    <td className="p-2">
                      <div className="font-semibold">{row.student}</div>
                      <div className="text-[10px] text-gray-400">{row.contract_id}</div>
                    </td>
                    <td className="p-2">{row.filial?.name}</td>
                    <td className="p-2">
                      <input
                        type="date"
                        value={row.session_start_date}
                        onChange={(event) =>
                          updateRow(
                            row.contract_id,
                            "session_start_date",
                            event.target.value
                          )
                        }
                        className="border rounded p-1 w-32"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="date"
                        value={row.session_end_date}
                        onChange={(event) =>
                          updateRow(
                            row.contract_id,
                            "session_end_date",
                            event.target.value
                          )
                        }
                        className="border rounded p-1 w-32"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="text"
                        value={row.plan_schedule}
                        onChange={(event) =>
                          updateRow(row.contract_id, "plan_schedule", event.target.value)
                        }
                        className="border rounded p-1 w-44"
                        placeholder="Optional"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.tuition_amount}
                        onChange={(event) =>
                          updateRow(row.contract_id, "tuition_amount", event.target.value)
                        }
                        className="border rounded p-1 w-24"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.tuition_discount}
                        onChange={(event) =>
                          updateRow(row.contract_id, "tuition_discount", event.target.value)
                        }
                        className="border rounded p-1 w-24"
                      />
                    </td>
                    <td className="p-2">
                      <select
                        value={row.payment_type}
                        onChange={(event) =>
                          updateRow(row.contract_id, "payment_type", event.target.value)
                        }
                        className="border rounded p-1 w-28"
                      >
                        <option value="full">Full</option>
                        <option value="installments">Installments</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </InputLineGroup>
  );
}
